import {IPoint} from "../interfaces/IPoint";
import {Log} from "./Log";
import {Delaunay} from "..";
import {Geodesic} from "./Geodesic";
import {ITriangulationInterpolation} from "../interfaces/ITriangulationInterpolation";
import {Vector} from "../entities/Vector";


const BYTECODE_GENERATION_DENSITY = 1;


export class Triangulation {

    public delaunay : Delaunay;
    public pointList: IPoint[];

    /**
     * Constructs the triangulation based on the duets.
     * @param pointList IPoint[]
     */
    constructor(pointList: IPoint[]) {
        // Log.log("Building initial triangulation with " + pointList.length + " duets.");
        this.delaunay = Delaunay.from(pointList);   //Delaunay from is from duet list []
        this.pointList = pointList;

    }

    /**
     * Reconstruct on change.
     */
    reconstruct(pointList: IPoint[]) {

        Log.log("Re-building duet list with " + pointList.length + " duets instead of " + this.pointList.length + ".")
        this.delaunay = Delaunay.from(pointList);   //Delaunay from is from duet list []
        this.pointList = pointList;
    }

    findRawIndex(point: IPoint) : number {
        return this.delaunay.find(point.lat, point.lon);
    }


    /**
     * @param point
     */
    interpolatePoint(point: IPoint) : ITriangulationInterpolation {
        let rawIndex = this.findRawIndex(point);    //Seek for the closest point index on the raw data
        let duetOnRawTriangulation = this.pointList[rawIndex];  //This is the closest point on raw data.

        // let SPTolerance = -1E-2; //Scalar product tolerance. Sometimes the scalar product can be very close to 0, which mean we're in place, while not actually being positive from left or right.
        let debugData:any = null;
        /**
         * Available values :
         * IN_PLACE: interpolated point is exactly the same as the one at the raw index.
         * BEFORE: interpolated point is before the one at the raw index (between the one at the raw index and raw index - 1)
         * AFTER : interpolated point is after the one at the raw index (between raw index and raw index +1)
         * @type {string}
         */
        let relativePosition: "BEFORE"|"AFTER"|"IN_PLACE";
        // const closestToSourcePointVector = new Vector(this.pointList[rawIndex], point);

        if(rawIndex > 0 && rawIndex < this.pointList.length - 1) {
            //Closest point is "in the middle" of the point list.
            const beforeVector = new Vector(this.pointList[rawIndex], this.pointList[rawIndex - 1]);
            const afterVector = new Vector(this.pointList[rawIndex], this.pointList[rawIndex + 1]);
            const beforeProj = Geodesic.projectOnVector(point, beforeVector);
            const afterProj = Geodesic.projectOnVector(point, afterVector);

            if(Geodesic.isOnVector(beforeProj, beforeVector)) {
                relativePosition = "BEFORE";
            } else if(Geodesic.isOnVector(afterProj, afterVector)) {
                relativePosition = "AFTER";
            } else {
                relativePosition = "IN_PLACE";
            }
        } else {
            if(rawIndex === 0) {
                const afterVector = new Vector(this.pointList[rawIndex], this.pointList[rawIndex + 1]);
                const afterProj = Geodesic.projectOnVector(point, afterVector);
                if(Geodesic.isOnVector(afterProj, afterVector)) {
                    relativePosition = "AFTER";
                } else {
                    relativePosition = "IN_PLACE";
                }
            } else {
                const beforeVector = new Vector(this.pointList[rawIndex], this.pointList[rawIndex - 1]);
                const beforeProj = Geodesic.projectOnVector(point, beforeVector);
                if(Geodesic.isOnVector(beforeProj, beforeVector)) {
                    relativePosition = "BEFORE";
                } else {
                    relativePosition = "IN_PLACE";
                }
            }
        }

        let heading: number = 0;

        if(relativePosition === "IN_PLACE") {
            heading = rawIndex === 0 ?
                Geodesic.computeHeading(new Vector(this.pointList[rawIndex], this.pointList[rawIndex + 1])) :
                Geodesic.computeHeading(new Vector(this.pointList[rawIndex - 1], this.pointList[rawIndex]));
            //Default behavior if in place : compute heading between previous point and next point except if first point (points 1 and 2)
            let iGap = Geodesic.metersBetween(point, duetOnRawTriangulation);
            let refGap = Geodesic.metersBetween(point, duetOnRawTriangulation);
            return {
                heading: heading,
                iGap: iGap,
                refGap: refGap,
                localRefIndex : rawIndex,
                refPoint: duetOnRawTriangulation,
                interpolation : duetOnRawTriangulation,
                relativePosition : relativePosition,
                sourcePoint: point,
                debugIndex: -1,
                debugData: debugData
            };    //If we're in place, no need to search for more.
        } else {

            let denseDuets = relativePosition === "BEFORE" ?
                Geodesic.increaseDuetListDensity([this.pointList[rawIndex - 1], this.pointList[rawIndex]], BYTECODE_GENERATION_DENSITY) :
                Geodesic.increaseDuetListDensity([this.pointList[rawIndex], this.pointList[rawIndex + 1]], BYTECODE_GENERATION_DENSITY) ;

            heading = relativePosition === "BEFORE" ?
                    Geodesic.computeHeading(new Vector(this.pointList[rawIndex - 1], this.pointList[rawIndex])) :
                    Geodesic.computeHeading(new Vector(this.pointList[rawIndex], this.pointList[rawIndex + 1]))

            let indexOnDenseDuets = Delaunay.from(denseDuets).find(point.lat, point.lon);
            let duetOnFineTriangulation = denseDuets[indexOnDenseDuets];
            let iGap = Geodesic.metersBetween(point, duetOnFineTriangulation);
            let refGap = Geodesic.metersBetween(point, duetOnRawTriangulation);

            return {
                heading: heading,
                iGap: iGap,
                refGap: refGap,
                localRefIndex : rawIndex,
                refPoint: duetOnRawTriangulation,
                interpolation : duetOnFineTriangulation,
                relativePosition : relativePosition,
                sourcePoint: point,
                debugIndex: indexOnDenseDuets,
                debugData: debugData
            };    //This is the d3 delaunay from, that's why we don't inject duet directly
        }
    }


    /**
     * Append a duet in the triangulation if there is not already a close duet on this triangulation.
     */
    appendInterpolationIfRoomAvailable(interpolation: ITriangulationInterpolation) {
        //1. Seek on the triangulation if we don't already have a close or collapsed point that would match
        let rawMatchingDuet = this.pointList[interpolation.localRefIndex];
        if(Geodesic.metersBetween(rawMatchingDuet, interpolation.interpolation) < 2*BYTECODE_GENERATION_DENSITY + 1E-1) { //Very important : the resolution is correct at 2x BYTECODE_GENERATION_DENSITY
            Log.log("No room is available to append this duet. (therefore no need to append it.)");
        } else {
            Log.log("interpolation can be inserted.");
            let rebuiltDuets: IPoint[] = [];
            for(let i = 0; i < this.pointList.length; i++) {
                if(interpolation.relativePosition === "BEFORE" && i === interpolation.localRefIndex) {
                    //We must add the interpolated duet before.
                    rebuiltDuets.push(interpolation.interpolation);
                }
                rebuiltDuets.push(this.pointList[i]);
                if(interpolation.relativePosition === "AFTER" && i === interpolation.localRefIndex) {
                    //We must add the interpolated duet after.
                    rebuiltDuets.push(interpolation.interpolation);
                }
            }
            this.reconstruct(rebuiltDuets);
        }
    }


    dumpPointList() : IPoint[]{
        return this.pointList;
    }
}
